<template>
  <figure class="viz-card">
    <figcaption class="viz-header">
      <h3>{{ title }}</h3>
      <span v-if="note" class="viz-note">{{ note }}</span>
      <div v-if="$slots.actions" class="viz-actions"><slot name="actions" /></div>
    </figcaption>

    <div v-if="loading" class="viz-empty">Loading…</div>
    <div v-else-if="!buckets.length" class="viz-empty">{{ emptyText }}</div>

    <!-- Always mounted (v-show, not v-if/v-else) - see BarChart.vue for why. -->
    <apexchart
      v-show="!loading && buckets.length"
      type="line"
      height="300"
      class="apex-clickable"
      :options="chartOptions"
      :series="chartSeries"
      @markerClick="onMarkerClick"
      @dataPointSelection="onPointSelection"
    />
  </figure>
</template>

<script>
import { PALETTE } from './chartPalette.js';

// Maps the legacy `colorVar` CSS custom-property name (e.g. "--series-1") to
// a literal hex value ApexCharts can render, so callers built around the old
// hand-rolled SVG chart's series definitions keep working unchanged.
const COLOR_VAR_MAP = {
  '--series-1': PALETTE.series1,
  '--series-2': PALETTE.series2,
};

export default {
  name: 'LineChart',
  props: {
    title: { type: String, required: true },
    series: { type: Array, default: () => [] }, // [{ key, label, colorVar }]
    buckets: { type: Array, default: () => [] }, // [{ label, start, end, [seriesKey]: value }]
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'No data available.' },
    note: { type: String, default: '' },
    formatValue: { type: Function, default: null },
  },
  emits: ['point-click'],
  computed: {
    resolvedColors() {
      return this.series.map((s) => COLOR_VAR_MAP[s.colorVar] || PALETTE.series1);
    },
    chartSeries() {
      return this.series.map((s) => ({
        name: s.label,
        data: this.buckets.map((b) => b[s.key] || 0),
      }));
    },
    chartOptions() {
      const format = this.format;
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { easing: 'easeout', speed: 300 },
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        },
        colors: this.resolvedColors,
        stroke: { width: 2.5, curve: 'smooth' },
        markers: {
          size: 4,
          strokeWidth: 2,
          strokeColors: 'var(--surface-1)',
          hover: { size: 6 },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.buckets.map((b) => b.label),
          labels: { style: { colors: PALETTE.textMuted, fontSize: '10px' } },
          axisBorder: { color: PALETTE.gridline },
          axisTicks: { color: PALETTE.gridline },
        },
        yaxis: {
          labels: { style: { colors: PALETTE.textMuted, fontSize: '11px' }, formatter: (val) => format(val) },
        },
        grid: { borderColor: PALETTE.gridline },
        legend: {
          position: 'bottom',
          fontSize: '12.5px',
          markers: { size: 6 },
          itemMargin: { horizontal: 10, vertical: 6 },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: (val) => format(val) },
        },
      };
    },
  },
  methods: {
    format(value) {
      return this.formatValue ? this.formatValue(value) : value.toLocaleString();
    },
    emitPointClick(seriesIndex, dataPointIndex) {
      const seriesDef = this.series[seriesIndex];
      const bucket = this.buckets[dataPointIndex];
      if (!seriesDef || !bucket) return;
      this.$emit('point-click', { series: seriesDef, bucket, bucketIndex: dataPointIndex });
    },
    // Fires when a marker (data point dot) is clicked directly.
    onMarkerClick(event, chartContext, { seriesIndex, dataPointIndex }) {
      this.emitPointClick(seriesIndex, dataPointIndex);
    },
    // Fires for a click anywhere on a series' hit area (e.g. between markers),
    // covering the gap left by markerClick's smaller hit target.
    onPointSelection(event, chartContext, config) {
      this.emitPointClick(config.seriesIndex, config.dataPointIndex);
    },
  },
};
</script>

<style scoped>
.viz-card {
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 18px 20px 20px;
  margin: 0;
  flex: 1 1 420px;
  min-width: 0; /* let this shrink below its content's intrinsic width instead of forcing the row wider / wrapping a sibling out of view */
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
  transition: box-shadow 0.15s ease;
}

.viz-card:hover {
  box-shadow: 0 6px 16px rgba(11, 11, 11, 0.07);
}

.viz-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--gridline);
}

.viz-actions {
  margin-left: auto;
  /* negative margin keeps the header the same height as cards without controls */
  margin-top: -8px;
  margin-bottom: -8px;
  align-self: center;
}

.viz-header h3 {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.viz-note {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
}

.viz-empty {
  font-size: 13px;
  color: var(--text-muted);
  padding: 24px 0;
  text-align: center;
}

.apex-clickable :deep(.apexcharts-marker) {
  cursor: pointer;
}
</style>
